import { Badge, createStyles, makeStyles, Theme, Tooltip } from "@material-ui/core";
import MuiTimelineConnector from "@material-ui/lab/TimelineConnector";
import MuiTimelineContent from "@material-ui/lab/TimelineContent";
import MuiTimelineDot from "@material-ui/lab/TimelineDot";
import MuiTimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import clsx from "clsx";
import { COLORS } from "constants/colors";
import useText from "constants/text";
import { ParcelsCount, ParcelsCountText } from "domains/Dashboard/components/Styles";
import { AddressPickup } from "domains/Tasks/details/styles";
import { Axe, EActionType, Stop as StopType } from "generated/graphql";
import styled from "styled-components";
import React from "react";

export const TimelineItem = styled(MuiTimelineItem)`
  min-height: 30px;
  margin-left: 10px;
  justify-content: space-between;
  &.dot {
    min-height: 20px;
  }
  :before {
    content: none;
  }
`;

export const TimelineContent = styled(MuiTimelineContent)`
  color: #707070;
  font-size: 11px;
  font-weight: 900;
  height: fit-content;
  margin-left: 22px;
  padding: 0 5px;
  position: absolute;
  top: 2px;
`;

export const TimelineConnector = styled(MuiTimelineConnector)`
  width: 1px;
  &.dot {
    margin-left: 7px;
  }
`;

export const TimelineDot = styled(MuiTimelineDot)<{ customColor: string }>`
  align-items: center;
  background-color: transparent;
  box-shadow: none;
  color: ${(props) => props?.customColor || COLORS.primary} !important;
  display: flex;
  font-size: 10px;
  font-weight: bold;
  justify-content: center;
  height: 18px;
  margin: 0;
  width: 18px;
  border-color: ${(props) => props?.customColor || COLORS.primary} !important;

  &.pickup.outlined {
    color: ${(props) => props?.customColor || COLORS.primary} !important;
    border-color: ${(props) => props?.customColor || COLORS.primary} !important;
  }

  &.pickup.contained {
    background-color: ${(props) => props?.customColor || COLORS.primary} !important;
    color: #fff !important;
  }

  &.delivery.outlined {
    color: #ebae23;
    border-color: #ebae23;
  }

  &.delivery.contained {
    background-color: #ebae23;
  }
  &.done {
    color: #c9c9c9;
    border-color: #c9c9c9;
  }

  &.dot {
    height: 5px;
    margin-left: 7px;
    padding: 0;
    width: 4.5px;
  }
`;

export const StopLabel = {
  [EActionType.Pickup]: "P",
  [EActionType.Delivery]: "D",
  [EActionType.PickupDelivery]: "P/D",
  [EActionType.Unknown]: "S",
};

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    customBadge: {
      backgroundColor: "transparent",
      color: "#399ebf",
      margin: "0 5px",
    },
  })
);

interface StopProps {
  stop: any;
  isLast: boolean;
  showItems?: boolean;
  onChangeTodo?: (value: string, stop: StopType) => void;
  primaryColor?: string;
  editMode?: boolean;
  axe?: Axe;
  templateStops?: StopType[];
  shouldShowActions?: boolean;
  isTaskDone?: boolean;
  getNumberOfProductsToPick?: () => number;
  onShowStop?: (stop: StopType) => void;
}

const Stop = ({ stop, isLast, primaryColor }: StopProps) => {
  const styles = useStyles();
  const { text } = useText();

  const getItemsTotalCount = (items: any[]) =>
    items?.reduce((total, curr) => {
      return total + curr.counter;
    }, 0);
  return (
    <>
      {stop && (
        <div>
          <TimelineItem>
            <TimelineSeparator>
              <TimelineDot
                customColor={stop?.isDisabled ? COLORS.gray : primaryColor}
                className={clsx("pickup", {
                  outlined: StopLabel[stop.action] === "P",
                  contained:
                    StopLabel[stop.action] === "P" ||
                    StopLabel[stop?.action] === "D" ||
                    StopLabel[stop?.action] === "P/D" ||
                    StopLabel[stop.action] === StopLabel.UNKNOWN ||
                    !StopLabel[stop.action],
                })}
              >
                {!stop.action || StopLabel[stop.action] === StopLabel.UNKNOWN
                  ? ""
                  : StopLabel[stop.action] || StopLabel.UNKNOWN}
              </TimelineDot>
              {!isLast && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Tooltip title={stop?.address?.address}>
                <AddressPickup>{`${stop?.address?.address.substring(0, 25)}...`}</AddressPickup>
              </Tooltip>
            </TimelineContent>

            <ParcelsCount
              backgroundColor="transparant"
              borderRadius="6px"
              color="#399ebf"
              padding="10px"
              marginTop="0px"
            >
              <ParcelsCountText color="#399ebf">
                <Badge
                  classes={{ badge: styles.customBadge }}
                  badgeContent={
                    StopLabel[stop?.action] === "P"
                      ? stop?.itemsToPick && getItemsTotalCount(stop?.itemsToPick)
                      : StopLabel[stop?.action] === "D"
                      ? stop?.itemsToDelivered && getItemsTotalCount(stop?.itemsToDelivered)
                      : 0
                  }
                  color="secondary"
                ></Badge>
                {` ${text.requestQuote.product}`}
              </ParcelsCountText>
            </ParcelsCount>
          </TimelineItem>

          {!isLast && (
            <TimelineItem className="dot">
              <TimelineSeparator>
                <TimelineDot
                  customColor={stop.isDisabled ? COLORS.gray : primaryColor}
                  className={clsx("dot", "outlined", "pickup")}
                />
                {!isLast && <TimelineConnector className="dot" />}
              </TimelineSeparator>
              <TimelineContent />
            </TimelineItem>
          )}
        </div>
      )}
    </>
  );
};

Stop.defaultProps = {
  showItems: true,
};

export default Stop;
